import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, FileText, CalendarCheck, Mail, Wrench, Trash2, Loader2, RefreshCw } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";

const TABS = [
  { key: "quotes", label: "Devis", icon: FileText, endpoint: "/admin/quotes" },
  { key: "depannage", label: "Dépannages", icon: Wrench, endpoint: "/admin/depannage" },
  { key: "appointments", label: "Rendez-vous", icon: CalendarCheck, endpoint: "/admin/appointments" },
  { key: "contacts", label: "Messages", icon: Mail, endpoint: "/admin/contacts" },
];

const STATUS_OPTIONS = ["nouveau", "en attente", "traité", "annulé"];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadItems = useCallback((t) => {
    setLoading(true);
    const ep = TABS.find((x) => x.key === t).endpoint;
    api.get(ep).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(tab); }, [tab, loadItems]);

  const onLogout = async () => { await logout(); nav("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/${tab}/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
      loadStats();
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/${tab}/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      loadStats();
      toast.success("Supprimé");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="light" />
            <span className="text-white/40 text-sm hidden sm:inline">Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-sm hidden sm:inline">{user?.email}</span>
            <button onClick={onLogout} data-testid="logout-btn" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm font-bold transition-colors">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          {[
            { label: "Devis", v: stats?.quotes, sub: `${stats?.quotes_new || 0} nouveaux`, icon: FileText },
            { label: "Dépannages", v: stats?.depannage, sub: `${stats?.depannage_new || 0} nouveaux`, icon: Wrench },
            { label: "Rendez-vous", v: stats?.appointments, sub: `${stats?.appointments_pending || 0} en attente`, icon: CalendarCheck },
            { label: "Messages", v: stats?.contacts, sub: `${stats?.contacts_new || 0} nouveaux`, icon: Mail },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-border rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-sm font-semibold">{s.label}</span>
                <s.icon className="w-5 h-5 text-brand" />
              </div>
              <p className="font-display text-4xl font-black text-navy mt-2">{s.v ?? "—"}</p>
              <p className="text-brand text-xs font-bold mt-1">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex gap-2 bg-white border border-border rounded-full p-1.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${tab === t.key ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
          <button onClick={() => { loadItems(tab); loadStats(); }} className="flex items-center gap-2 text-navy/60 hover:text-navy text-sm font-semibold">
            <RefreshCw className="w-4 h-4" /> Actualiser
          </button>
        </div>

        {/* List */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-text-muted">Aucune demande pour le moment.</div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((it) => (
                <div key={it.id} className="p-5 md:p-6 hover:bg-surface transition-colors" data-testid={`item-${it.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-bold text-navy">{it.name}</span>
                        <StatusBadge status={it.status} />
                      </div>
                      <div className="text-sm text-text-muted mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <a href={`tel:${it.phone}`} className="hover:text-brand">{it.phone}</a>
                        <a href={`mailto:${it.email}`} className="hover:text-brand">{it.email}</a>
                        {it.city && <span>{it.city}</span>}
                        {it.service && <span className="font-semibold text-navy">{it.service}</span>}
                      </div>
                      {tab === "appointments" && (
                        <p className="text-sm font-bold text-brand mt-1">📅 {it.date} à {it.slot} {it.address && `— ${it.address}`}</p>
                      )}
                      {tab === "depannage" && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {it.equipment && <span className="text-xs font-bold bg-navy/5 text-navy px-2.5 py-1 rounded-full">🔧 {it.equipment}</span>}
                          {it.issue && <span className="text-xs font-bold bg-brand/10 text-brand px-2.5 py-1 rounded-full">⚠ {it.issue}</span>}
                          {it.weekend && <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">Week-end / férié</span>}
                        </div>
                      )}
                      {it.subject && <p className="text-sm text-navy font-semibold mt-1">Sujet : {it.subject}</p>}
                      {it.message && <p className="text-sm text-text-muted mt-2 bg-surface rounded-lg p-3">{it.message}</p>}
                      <p className="text-xs text-slate-400 mt-2">{new Date(it.created_at).toLocaleString("fr-FR")}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select value={it.status} onChange={(e) => updateStatus(it.id, e.target.value)} data-testid={`status-${it.id}`} className="text-sm border border-border rounded-lg py-2 px-3 bg-white focus:outline-none focus:ring-2 focus:ring-brand">
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    nouveau: "bg-brand/10 text-brand",
    "en attente": "bg-amber-100 text-amber-700",
    "traité": "bg-green-100 text-green-700",
    "annulé": "bg-slate-100 text-slate-500",
  };
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}
